import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getAccessToken } from '../services/api';

// ═══════════════════════════════════════════════════════════════
// U2: WebSocket hook for real-time notifications
// ═══════════════════════════════════════════════════════════════

const WS_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000').replace(/\/$/, '');

export interface WebSocketNotification {
  type: string;
  title: string;
  message: string;
  [key: string]: any;
}

export function useWebSocket() {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState<WebSocketNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    const socket = io(WS_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('notification', (data: WebSocketNotification) => {
      setNotifications((prev) => [data, ...prev].slice(0, 50));
      setUnreadCount((prev) => prev + 1);
    });

    socket.on('badge-unlock', (data: any) => {
      setNotifications((prev) => [{
        type: 'badge-unlock',
        title: `Badge desbloqueado: ${data.name}`,
        message: data.description || '',
        ...data,
      }, ...prev].slice(0, 50));
      setUnreadCount((prev) => prev + 1);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const markAllRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return {
    connected,
    notifications,
    unreadCount,
    markAllRead,
    clearNotifications,
  };
}
